import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Modal } from 'react-bootstrap';
import { ImageOff, ImageUp, KeyRound, Layers, Move, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { PageLoader } from '../../Components/LoadingSpinner';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import ActionsMenu, { ActionsMenuItem } from '../../Components/ActionsMenu';
import BlueprintWithMarkers, { NumberedMarker, NumberedMarkerGlyph } from '../../Components/BlueprintWithMarkers';
import BlueprintUploadModal, { DeferredBlueprintUpload } from '../../Components/BlueprintUploadModal';
import BlueprintRelocationWizard, { RelocationDrawing } from '../../Components/BlueprintRelocationWizard';
import { RelocatableMarker, areaRelocationItems } from '../../Helpers/blueprintRelocation';
import { RelocationPlacement, abortBlueprintRelocation, commitBlueprintRelocation } from '../../api/blueprintRelocationApi';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useOpenPath } from '../../hooks/useOpenPath';
import { useParking } from '../../hooks/parking';
import { useBuildingLeases } from '../../hooks/leases';
import { endedLeasesForObject, leasesPath } from '../../Helpers/leaseUtils';
import { useTenants } from '../../hooks/tenants';
import { PARKING_BLUEPRINT_ENTITY_TYPE, parkingApi, StaleBlueprintError } from '../../api/parkingApi';
import { LeaseObjectType, ParkingBlueprintSource, ParkingAreaKind } from '../../types/Enums';
import { translateParkingAreaKind } from '../../types/EnumTranslations';
import { BlueprintLocation, PhotoModel } from '../../types/protocols';
import { ParkingAreaInput, ParkingSpot, ParkingSpotInput } from '../../types/parking';
import LeaseModal, { LeaseModalSubmitData } from '../Leases/LeaseModal';
import LeaseQueueCloseModal from '../ParkingQueue/LeaseQueueCloseModal';
import { useParkingQueueMutations } from '../../hooks/parkingQueue';
import AddToQueueModal from '../ParkingQueue/AddToQueueModal';
import { entryPrefillForSpot, SpotEntryPrefill, suggestedLeaseStart } from '../../Helpers/parkingQueueUtils';
import { Lease } from '../../types/leases';
import { ParkingQueueEntry, ParkingQueueEntryInput } from '../../types/parkingQueue';
import {
    AREA_BLUEPRINT_STATUS_TEXT,
    drawingChangeConfirmationText,
    isLevelBlueprintSource,
    resolveAreaBlueprint,
    spotsHiddenByDrawingChange,
    nextSpotNumber,
    parkingAreaDisplayName,
    parkingSpotDisplayName,
    isSpotRented,
    sortedParkingSpots,
    spotHasCharger,
    spotStatusColor,
    spotTypeIcon,
    utilitiesOnAreaDrawing,
    vacantSpotCount,
} from '../../Helpers/parkingUtils';
import { BaseComponentImpl } from '../../types/implementations';
import ParkingAreaModal from './ParkingAreaModal';
import ParkingSpotModal from './ParkingSpotModal';
import ParkingSpotList from './ParkingSpotList';
import ParkingSpotDetailModal from './ParkingSpotDetailModal';
import { parkingAreaRoomLabel } from './parkingAreaLabels';
import '../../Components/LevelNavigator.css';
import './Parking.css';

/**
 * Drawing interaction modes, each started and ended explicitly:
 * - new:   click the drawing to create spots in series
 * - place: click the drawing to place one (unplaced) spot
 * - move:  drag existing markers to new positions ("Flytta markörer")
 */
type PlacementMode = { kind: 'none' } | { kind: 'new' } | { kind: 'place'; spotKey: string } | { kind: 'move' };

/** Drawing actions; each may need the hidden-spots confirmation. */
type DrawingAction = 'useLevel' | 'stopLevel' | 'uploadOwn';

type ConfirmAction =
    | { kind: 'area' }
    | { kind: 'spot'; spotKey: string }
    | { kind: 'blueprint' }
    | { kind: 'drawing'; action: DrawingAction; count: number };

/** One parking area: drawing with numbered markers, spot list and spot details. */
const ParkingAreaPage: React.FC = () => {
    const { id: buildingKey, areaKey } = useParams<{ id: string; areaKey: string }>();
    const navigate = useNavigate();
    const openPath = useOpenPath();
    const isAdmin = useIsAdmin();
    const [user] = useAuthState(auth);
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const parking = useParking({ buildingKey });
    // Destructured so callbacks below depend on stable functions, not on the
    // hook's result object (which is new every render).
    const { areas, sortedAreas, error, submitting, clearError, setSpotLocation, stampAreaUpdated, refresh: refreshParking } = parking;
    // Leases of the building: the spot panel shows the current lease, the
    // modal creates one from a spot. Every lease write also refetches the
    // building so currentLeaseKey badges and counts update (plan D11).
    const leaseHook = useBuildingLeases({ buildingKey, onMutated: refreshParking });
    const { getTenantName } = useTenants();

    // ?spot= opens a spot (links from the level overview).
    const [searchParams] = useSearchParams();
    // The spot a click acted on: the one the detail modal shows, or the one
    // placement and move mode work on. Only a click outside those modes opens the modal (`spotModalOpen`).
    const [selectedSpotKey, setSelectedSpotKey] = useState<string | null>(() => searchParams.get('spot'));
    const [spotModalOpen, setSpotModalOpen] = useState(() => searchParams.has('spot'));
    // The spot under the mouse, in the list or on the drawing.
    const [hoveredSpotKey, setHoveredSpotKey] = useState<string | null>(null);
    const [placement, setPlacement] = useState<PlacementMode>({ kind: 'none' });
    const [pendingLocation, setPendingLocation] = useState<BlueprintLocation | undefined>(undefined);
    const [lastCreatedNumber, setLastCreatedNumber] = useState<string | null>(null);
    const [showAreaModal, setShowAreaModal] = useState(false);
    const [showSpotModal, setShowSpotModal] = useState<'closed' | 'new' | 'edit'>('closed');
    const [showUpload, setShowUpload] = useState(false);
    const [droppedFile, setDroppedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
    const [vacantOnly, setVacantOnly] = useState(false);
    // Level mode only: the level's utilities on the drawing, read-only. Off by
    // default like "Visa komponenter" on the level page; linked chargers show regardless.
    const [showUtilities, setShowUtilities] = useState(false);
    const [showLeaseModal, setShowLeaseModal] = useState(false);
    // The lease just written, while asking whether it fills one of the tenant's köplatser.
    const [queueCheckLease, setQueueCheckLease] = useState<Lease | null>(null);
    // Hyr ut in the spot modal's Kö section: the applicant picked while their lease form is open,
    // and a counter that makes the section read the queue again once their lease is linked.
    const [queuePick, setQueuePick] = useState<{ entry: ParkingQueueEntry; reason?: string } | null>(null);
    const [queueReloadKey, setQueueReloadKey] = useState(0);
    // "Lägg till i kön": the prefill is fixed when the form opens (a new object would reset the form).
    const [addToQueuePrefill, setAddToQueuePrefill] = useState<SpotEntryPrefill | null>(null);
    const queueMutations = useParkingQueueMutations(async () => undefined);
    // Own-drawing upload in progress: the drawing node exists but is not yet
    // current; the wizard moves the spot markers and one commit switches.
    const [relocation, setRelocation] = useState<{
        source: RelocationDrawing;
        target: RelocationDrawing;
        items: RelocatableMarker[];
        upload: DeferredBlueprintUpload;
    } | null>(null);
    const [relocationError, setRelocationError] = useState<string | null>(null);

    useEffect(() => {
        if (buildingKey && (!currentBuilding || currentBuilding.key !== buildingKey)) {
            fetchBuilding(buildingKey);
        }
    }, [buildingKey, currentBuilding, fetchBuilding]);

    const area = areaKey ? areas[areaKey] : undefined;
    const buildingName = currentBuilding?.summary?.name || 'Byggnad';
    const areaName = area ? parkingAreaDisplayName(area) : 'Parkeringsområde';
    useDocumentTitle(`${areaName} - ${buildingName}`);

    // Every read of the area's drawing goes through the resolver.
    const structure = currentBuilding?.model.structure;
    const resolution = useMemo(() => (area ? resolveAreaBlueprint(area, structure) : undefined), [area, structure]);
    const currentBlueprint = resolution?.status === 'ok' ? resolution.image : undefined;
    const currentBlueprintKey = resolution?.status === 'ok' ? resolution.key : undefined;
    // Level mode: placements re-check the level's current drawing key before writing (the cached key may be stale).
    const levelGuardKey = resolution?.status === 'ok' && resolution.owner === 'level' ? resolution.levelKey : undefined;
    const levelGuard = useMemo(() => (levelGuardKey ? { levelKey: levelGuardKey } : undefined), [levelGuardKey]);
    const spots = useMemo(() => (area ? sortedParkingSpots(area) : []), [area]);
    const selectedSpot = selectedSpotKey && area?.spots ? area.spots[selectedSpotKey] : undefined;
    const selectedLease = selectedSpot?.currentLeaseKey
        ? leaseHook.leases.find(l => l.key === selectedSpot.currentLeaseKey)
        : undefined;
    const selectedEndedLeases = isAdmin && area && selectedSpot
        ? endedLeasesForObject(leaseHook.leases, { type: LeaseObjectType.ParkingSpot, areaKey: area.key, key: selectedSpot.key })
        : undefined;
    const vacantCount = area ? vacantSpotCount(area) : 0;

    // A ?spot= key that is not in the area must not open the modal for a spot selected later by placement.
    useEffect(() => {
        if (area && spotModalOpen && !selectedSpot) setSpotModalOpen(false);
    }, [area, spotModalOpen, selectedSpot]);

    // "Lediga" filter applies to both the list and the drawing; counts use all spots.
    const visibleSpots = useMemo(() => (vacantOnly ? spots.filter(spot => !isSpotRented(spot)) : spots), [spots, vacantOnly]);

    const spotMarkers = useMemo((): NumberedMarker[] => visibleSpots.map(spot => {
        const glyphs: NumberedMarkerGlyph[] = [];
        if (spotHasCharger(spot)) glyphs.push('charger');
        if (spot.hasOutlet === true) glyphs.push('outlet');
        return {
            key: spot.key,
            number: spot.number,
            color: spotStatusColor(spot),
            icon: spotTypeIcon(spot.type),
            glyphs,
            isRented: isSpotRented(spot),
            displayName: parkingSpotDisplayName(spot),
            blueprintLocations: spot.blueprintLocations,
        };
    }), [visibleSpots]);

    // Utilities with a pin on the drawing (level mode). `utilityMarkers` is what is
    // drawn; `levelHasUtilities` decides whether the toggle is offered at all.
    const utilityMarkers = useMemo(
        () => (area ? utilitiesOnAreaDrawing(structure, area, resolution, showUtilities).map(c => new BaseComponentImpl(c)) : []),
        [structure, area, resolution, showUtilities]
    );
    const levelHasUtilities = useMemo(
        () => (area ? utilitiesOnAreaDrawing(structure, area, resolution, true).length > 0 : false),
        [structure, area, resolution]
    );

    // Number suggestion for the next spot in a series: last created, else the highest existing.
    const suggestedNumber = useMemo(() => {
        const base = lastCreatedNumber ?? spots[spots.length - 1]?.number;
        return base ? (nextSpotNumber(base) ?? '') : '';
    }, [lastCreatedNumber, spots]);

    // Memoised so re-renders while the modal is open (e.g. `submitting`) do not
    // reset the form; ParkingSpotModal re-initialises when this identity changes.
    const newSpotInitialValues = useMemo((): Partial<ParkingSpotInput> | undefined => {
        if (showSpotModal !== 'new') return undefined;
        return {
            number: suggestedNumber,
            type: selectedSpot?.type,
            ceilingHeight: selectedSpot?.ceilingHeight,
            hasOutlet: selectedSpot?.hasOutlet,
        };
    }, [showSpotModal, suggestedNumber, selectedSpot?.type, selectedSpot?.ceilingHeight, selectedSpot?.hasOutlet]);

    const exitPlacement = useCallback(() => {
        setPlacement({ kind: 'none' });
        setPendingLocation(undefined);
    }, []);

    // A click in the list or on a marker; in placement or move mode it only highlights.
    const handleSpotClick = useCallback((spotKey: string) => {
        setSelectedSpotKey(spotKey);
        if (placement.kind === 'none') setSpotModalOpen(true);
    }, [placement.kind]);

    const handleBlueprintClick = useCallback(async (location: BlueprintLocation) => {
        if (!areaKey || !currentBlueprintKey) return;
        if (placement.kind === 'place') {
            const spotKey = placement.spotKey;
            exitPlacement();
            await setSpotLocation(areaKey, spotKey, currentBlueprintKey, location, levelGuard).catch(() => undefined);
            setSelectedSpotKey(spotKey);
            return;
        }
        if (placement.kind === 'new') {
            setPendingLocation(location);
            setShowSpotModal('new');
        }
    }, [areaKey, currentBlueprintKey, placement, setSpotLocation, exitPlacement, levelGuard]);

    // Move mode: a dropped marker is written immediately; the mode stays on until "Klar".
    const handleSpotMove = useCallback((spotKey: string, location: BlueprintLocation) => {
        if (!areaKey || !currentBlueprintKey) return;
        setSelectedSpotKey(spotKey);
        setSpotLocation(areaKey, spotKey, currentBlueprintKey, location, levelGuard).catch(() => undefined);
    }, [areaKey, currentBlueprintKey, setSpotLocation, levelGuard]);

    if (loadingBuilding && !currentBuilding) return <PageLoader />;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding || !buildingKey || !areaKey) return <p>Byggnaden hittades ej</p>;
    if (!area) {
        return (
            <div className="parking-page">
                <p>Parkeringsområdet hittades inte.</p>
                <Link to={`/building/${buildingKey}/parking`} className="btn btn-outline-secondary">Till parkering</Link>
            </div>
        );
    }

    // Own-drawing upload also writes blueprintSource 'own' with the pointer.
    const ownUploadTarget = parkingApi.ownBlueprintUploadTarget(buildingKey, areaKey);
    const hasDrawing = !!(currentBlueprint && currentBlueprintKey);
    const isLevelMode = isLevelBlueprintSource(area);
    const roomLabel = parkingAreaRoomLabel(area, structure);
    const noDrawingText = resolution && resolution.status !== 'ok'
        ? AREA_BLUEPRINT_STATUS_TEXT[resolution.status]
        : AREA_BLUEPRINT_STATUS_TEXT.noOwnDrawing;
    const canUseLevelDrawing = area.kind === ParkingAreaKind.Garage;

    // How many placed spots a drawing action would hide.
    const hiddenCountFor = (action: DrawingAction): number => {
        switch (action) {
            case 'useLevel':
                return spotsHiddenByDrawingChange(area, { ...area, blueprintSource: ParkingBlueprintSource.Level }, structure);
            case 'stopLevel':
                return spotsHiddenByDrawingChange(area, { ...area, blueprintSource: ParkingBlueprintSource.Own }, structure);
            case 'uploadOwn':
                // The relocation wizard moves the placed spots onto the new drawing; no confirmation up front.
                return 0;
        }
    };

    // Deferred upload: switch drawings now when nothing is placed, else open the wizard.
    const finishDrawingSwitch = async (upload: DeferredBlueprintUpload, placements: RelocationPlacement[]) => {
        await commitBlueprintRelocation({ imageKey: upload.key, pointerUpdates: upload.pointerUpdates, placements });
        setRelocation(null);
        await stampAreaUpdated(areaKey).catch(() => undefined);
    };

    const handleDeferredUpload = async (upload: DeferredBlueprintUpload) => {
        setRelocationError(null);
        const items = currentBlueprint && currentBlueprintKey ? areaRelocationItems(buildingKey, area, currentBlueprintKey) : [];
        if (!currentBlueprint || !currentBlueprintKey || items.length === 0) {
            try {
                await finishDrawingSwitch(upload, []);
            } catch (err) {
                setRelocationError(err instanceof Error ? err.message : 'Ritningen kunde inte bytas');
            }
            return;
        }
        setRelocation({
            source: { image: currentBlueprint, imageKey: currentBlueprintKey },
            target: { image: upload.blueprint, imageKey: upload.key },
            items,
            upload,
        });
    };

    const handleRelocationCommit = async (placements: Map<string, BlueprintLocation>) => {
        if (!relocation) return;
        await finishDrawingSwitch(
            relocation.upload,
            relocation.items.flatMap(item => {
                const location = placements.get(item.id);
                return location ? [{ dbPath: item.dbPath, location }] : [];
            })
        );
    };

    const handleRelocationAbort = async () => {
        if (!relocation) return;
        await abortBlueprintRelocation({ blueprintPath: relocation.upload.blueprintPath, entityType: PARKING_BLUEPRINT_ENTITY_TYPE });
        setRelocation(null);
    };

    const performDrawingAction = async (action: DrawingAction) => {
        exitPlacement();
        if (action === 'uploadOwn') {
            setShowUpload(true);
            return;
        }
        const source = action === 'useLevel' ? ParkingBlueprintSource.Level : ParkingBlueprintSource.Own;
        // The hook refetches the building afterwards.
        await parking.setBlueprintSource(areaKey, source).catch(() => undefined);
    };

    const startDrawingAction = (action: DrawingAction) => {
        const count = hiddenCountFor(action);
        if (count > 0) {
            setConfirm({ kind: 'drawing', action, count });
            return;
        }
        performDrawingAction(action);
    };

    const handleAreaSubmit = async (input: ParkingAreaInput) => {
        await parking.updateArea(areaKey, input);
    };

    const handleSpotSubmit = async (input: ParkingSpotInput) => {
        if (showSpotModal === 'edit' && selectedSpot) {
            await parking.updateSpot(areaKey, selectedSpot.key, input);
            return;
        }
        let spot: ParkingSpot;
        try {
            spot = await parking.createSpot(areaKey, input, pendingLocation, pendingLocation ? levelGuard : undefined);
        } catch (err) {
            if (err instanceof StaleBlueprintError) {
                // The building was refetched; the clicked point belongs to the old drawing.
                setPendingLocation(undefined);
                setShowSpotModal('closed');
            }
            throw err;
        }
        setLastCreatedNumber(spot.number);
        setSelectedSpotKey(spot.key);
        setPendingLocation(undefined);
        // Series placement: stay in placement mode until the user stops it.
    };

    const handleSpotModalHide = () => {
        setShowSpotModal('closed');
        setPendingLocation(undefined);
    };

    const handleConfirm = async () => {
        if (!confirm) return;
        const action = confirm;
        setConfirm(null);
        try {
            if (action.kind === 'area') {
                await parking.deleteArea(areaKey);
                navigate(`/building/${buildingKey}/parking`);
            } else if (action.kind === 'spot') {
                // Closed before the write so the detail modal does not come back for a spot being deleted.
                setSpotModalOpen(false);
                await parking.deleteSpot(areaKey, action.spotKey);
                if (selectedSpotKey === action.spotKey) setSelectedSpotKey(null);
            } else if (action.kind === 'blueprint' && currentBlueprintKey && !isLevelMode) {
                await parking.deleteCurrentBlueprint(areaKey, currentBlueprintKey);
            } else if (action.kind === 'drawing') {
                await performDrawingAction(action.action);
            }
        } catch {
            // Error is surfaced through the hook's error state.
        }
    };

    const handleLeaseSubmit = async ({ input, filesToUpload }: LeaseModalSubmitData) => {
        const lease = await leaseHook.createLeaseWithContracts(input, filesToUpload);
        setShowLeaseModal(false);
        setQueueCheckLease(lease);
    };

    /** Lease for the applicant picked in the queue; linking it closes their köplats. */
    const handleQueueLeaseSubmit = async ({ input, filesToUpload }: LeaseModalSubmitData) => {
        if (!queuePick || !buildingKey || !areaKey || !selectedSpot) return;
        const lease = await leaseHook.createLeaseWithContracts(input, filesToUpload);
        setQueuePick(null);
        try {
            await queueMutations.linkLease(queuePick.entry.key, {
                buildingKey, areaKey, spotKey: selectedSpot.key, leaseKey: lease.key, reason: queuePick.reason,
            });
        } catch {
            // The lease exists; the ordinary after-lease question lets the admin close the köplats instead.
            setQueueCheckLease(lease);
        } finally {
            setQueueReloadKey(k => k + 1);
        }
    };

    /** Ny köplats from the spot's Kö section; back to the spot with the list read again. */
    const handleAddToQueue = async (input: ParkingQueueEntryInput) => {
        try {
            await queueMutations.createEntry(input);
        } catch {
            return; // Shown in the form through the hook's error state.
        }
        setAddToQueuePrefill(null);
        setQueueReloadKey(k => k + 1);
    };

    const handleDeletePhoto = async (photo: PhotoModel) => {
        if (!selectedSpot) return;
        await parking.deleteSpotPhoto(areaKey, selectedSpot.key, photo.key);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (!isAdmin) return;
        const file = e.dataTransfer.files[0];
        if (!file) return;
        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
            setDroppedFile(file);
            startDrawingAction('uploadOwn');
        }
    };

    const confirmText = confirm?.kind === 'area'
        ? `Området och alla ${spots.length} platser tas bort. Detta kan inte ångras.`
        : confirm?.kind === 'spot'
            ? 'Platsen och dess foton tas bort. Detta kan inte ångras.'
            : confirm?.kind === 'drawing'
                ? `${drawingChangeConfirmationText(confirm.count)} Markörerna sparas och visas igen om ritningen byts tillbaka.`
                : 'Ritningen tas bort. Platsernas markörer på den sparas men visas inte längre.';
    const confirmIsDrawingChange = confirm?.kind === 'drawing';
    // Opened from the spot modal, which steps aside while they are open and returns after.
    const spotChildModalOpen = showSpotModal === 'edit' || showLeaseModal || !!queuePick || !!addToQueuePrefill || !!queueCheckLease || confirm?.kind === 'spot';
    const cancelConfirm = () => {
        setConfirm(null);
        // A dropped file waiting for the hidden-spots confirmation is discarded too.
        setDroppedFile(null);
    };

    // Rarely used, so behind the three-dot menu beside the drawing's toggles.
    const canPlace = hasDrawing && placement.kind === 'none';
    const drawingMenuItems: ActionsMenuItem[] = [
        ...(canPlace ? [{ label: 'Placera nya platser', icon: <Plus size={18} color="#6c757d" />, onClick: () => setPlacement({ kind: 'new' }) }] : []),
        ...(canPlace && spots.some(s => s.blueprintLocations?.[currentBlueprintKey!])
            ? [{ label: 'Flytta markörer', icon: <Move size={18} color="#6c757d" />, onClick: () => setPlacement({ kind: 'move' }) }]
            : []),
        ...(isLevelMode
            ? [
                { label: 'Ladda upp egen ritning', icon: <ImageUp size={18} color="#6c757d" />, onClick: () => startDrawingAction('uploadOwn') },
                { label: 'Sluta använda våningens ritning', icon: <ImageOff size={18} />, danger: true, onClick: () => startDrawingAction('stopLevel') },
            ]
            : [
                ...(canUseLevelDrawing ? [{
                    label: 'Använd våningens ritning',
                    icon: <Layers size={18} color="#6c757d" />,
                    disabled: !area.roomKey,
                    title: area.roomKey
                        ? 'Våningens fastighetsteknik kan då också visas på ritningen'
                        : 'Koppla området till ett rum under Redigera område först',
                    onClick: () => startDrawingAction('useLevel'),
                }] : []),
                ...(hasDrawing ? [
                    { label: 'Byt ritning', icon: <ImageUp size={18} color="#6c757d" />, onClick: () => startDrawingAction('uploadOwn') },
                    { label: 'Ta bort ritning', icon: <Trash2 size={18} />, danger: true, onClick: () => setConfirm({ kind: 'blueprint' }) },
                ] : []),
            ]),
    ];

    // Highlighted (row + marker): the hovered spot, else the one the open modal or placement mode is about.
    const highlightedSpotKey = hoveredSpotKey ?? (spotModalOpen || placement.kind !== 'none' ? selectedSpotKey : null);

    return (
        <>
            <BreadcrumbComponent
                items={[
                    { text: buildingName, link: `/building/${buildingKey}` },
                    { text: 'Parkering', link: `/building/${buildingKey}/parking` },
                    { text: areaName },
                ]}
            />

            <div className="parking-page">
                {/* The building's other areas, as the level page's pills: the current one dark, the rest links */}
                {sortedAreas.length > 1 && (
                    <div className="level-buttons-container parking-area-pills" role="group" aria-label="Parkeringsområden">
                        {sortedAreas.map(a => a.key === areaKey ? (
                            <span key={a.key} className="level-button level-button--current" aria-current="page">{parkingAreaDisplayName(a)}</span>
                        ) : (
                            <Link key={a.key} to={`/building/${buildingKey}/parking/${a.key}`} className="level-button">{parkingAreaDisplayName(a)}</Link>
                        ))}
                    </div>
                )}
                <div className="parking-title-container">
                    <h3>{areaName}</h3>
                    {/* The facts: beside the title on desktop, on their own line under it on phones */}
                    <div className="parking-stats">
                        <span className="parking-stat">{translateParkingAreaKind(area.kind)}</span>
                        <span className="parking-stat">{spots.length} platser</span>
                        <span className={`parking-stat ${vacantCount > 0 ? 'parking-stat--success' : 'parking-stat--muted'}`}>Lediga: {vacantCount}</span>
                        {area.defaultCeilingHeight && <span className="parking-stat">Fri höjd {area.defaultCeilingHeight.toFixed(2)} m</span>}
                        {roomLabel && (
                            <span className={`parking-stat ${area.roomKey && !structure?.rooms?.[area.roomKey] ? 'parking-stat--warning' : ''}`}>{roomLabel}</span>
                        )}
                        {isLevelMode && <span className="parking-stat">Våningens ritning</span>}
                    </div>
                    {isAdmin && (
                        <div className="parking-title-actions">
                            <ActionsMenu disabled={submitting} items={[
                                { label: 'Redigera område', icon: <Pencil size={18} color="#6c757d" />, onClick: () => setShowAreaModal(true) },
                                { label: 'Ta bort område', icon: <Trash2 size={18} />, danger: true, onClick: () => setConfirm({ kind: 'area' }) },
                            ]} />
                        </div>
                    )}
                </div>
                {area.comment && <p style={{ color: '#666' }}>{area.comment}</p>}

                {error && <Alert variant="danger" dismissible onClose={clearError}>{error}</Alert>}
                {relocationError && <Alert variant="danger" dismissible onClose={() => setRelocationError(null)}>{relocationError}</Alert>}
                {leaseHook.error && <Alert variant="danger" dismissible onClose={leaseHook.clearError}>{leaseHook.error}</Alert>}

                <div className="dropshadow parking-card">
                    <div className="parking-drawing-column">
                        <div className="parking-section-header">
                            <h4>Ritning</h4>
                            <div className="d-flex gap-2 flex-wrap">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    active={vacantOnly}
                                    onClick={() => setVacantOnly(v => !v)}
                                    title="Visa bara lediga platser"
                                >
                                    <KeyRound size={14} className="me-1" />Lediga
                                </Button>
                                {hasDrawing && levelHasUtilities && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        active={showUtilities}
                                        onClick={() => setShowUtilities(v => !v)}
                                        title="Visa våningens fastighetsteknik på ritningen"
                                    >
                                        <Wrench size={14} className="me-1" />Fastighetsteknik
                                    </Button>
                                )}
                            {isAdmin && !hasDrawing && !isLevelMode && (
                                <Button variant="outline-secondary" size="sm" onClick={() => startDrawingAction('uploadOwn')} disabled={submitting}>
                                    Ladda upp ritning
                                </Button>
                            )}
                            {isAdmin && <ActionsMenu disabled={submitting} items={drawingMenuItems} />}
                            </div>
                        </div>

                        {placement.kind !== 'none' && (
                            <div className={`parking-placement-banner ${placement.kind === 'move' ? 'parking-placement-banner--move' : ''}`}>
                                <span>
                                    {placement.kind === 'new' && 'Klicka på ritningen där nästa plats ligger. Numret föreslås automatiskt.'}
                                    {placement.kind === 'place' && `Klicka på ritningen där ${selectedSpot ? parkingSpotDisplayName(selectedSpot).toLowerCase() : 'platsen'} ska ligga.`}
                                    {placement.kind === 'move' && (
                                        <><strong>Flyttläge:</strong> dra en markör till rätt plats. Varje släpp sparas direkt. Avsluta med Klar.</>
                                    )}
                                </span>
                                <Button variant={placement.kind === 'place' ? 'outline-secondary' : 'primary'} size="sm" onClick={exitPlacement}>
                                    {placement.kind === 'place' ? 'Avbryt' : 'Klar'}
                                </Button>
                            </div>
                        )}

                        {hasDrawing ? (
                            <BlueprintWithMarkers
                                blueprint={currentBlueprint}
                                currentBlueprintKey={currentBlueprintKey}
                                building={currentBuilding}
                                spots={spotMarkers}
                                selectedSpotKey={highlightedSpotKey}
                                onSpotClick={handleSpotClick}
                                onSpotHover={setHoveredSpotKey}
                                utilities={utilityMarkers}
                                onUtilityClick={(componentKey, event) => openPath(`/building/${buildingKey}/component/${componentKey}`, event)}
                                placementMode={placement.kind === 'new' || placement.kind === 'place'}
                                onBlueprintClick={handleBlueprintClick}
                                onSpotMove={placement.kind === 'move' && isAdmin ? handleSpotMove : undefined}
                                title=""
                            />
                        ) : (
                            <div
                                className={`parking-blueprint-placeholder ${isDragOver ? 'parking-blueprint-placeholder--drag-over' : ''}`}
                                onDragOver={e => { e.preventDefault(); if (isAdmin) setIsDragOver(true); }}
                                onDragLeave={e => { e.preventDefault(); setIsDragOver(false); }}
                                onDrop={handleDrop}
                            >
                                <span>{isDragOver ? 'Släpp för att ladda upp' : noDrawingText}</span>
                                {isAdmin && (
                                    <>
                                        <Button variant="primary" size="sm" onClick={() => startDrawingAction('uploadOwn')}>
                                            {isLevelMode ? 'Ladda upp egen ritning' : 'Ladda upp'}
                                        </Button>
                                        <small>
                                            eller dra och släpp en bild eller PDF här.
                                            {isLevelMode
                                                ? ' Området använder våningens ritning; ladda upp ritningen på våningen för att den ska visas här.'
                                                : ' I appen kan även satellitbilden användas.'}
                                        </small>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="parking-side-column">
                        <div className="parking-spot-list-section">
                            <div className="parking-section-header">
                                <h4>Platser</h4>
                                {isAdmin && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => { setPendingLocation(undefined); setShowSpotModal('new'); }}
                                        disabled={submitting}
                                    >
                                        <Plus size={14} className="me-1" />Ny plats
                                    </Button>
                                )}
                            </div>
                            <ParkingSpotList
                                spots={visibleSpots}
                                selectedSpotKey={highlightedSpotKey}
                                currentBlueprintKey={hasDrawing ? currentBlueprintKey : undefined}
                                onSelect={handleSpotClick}
                                onHover={setHoveredSpotKey}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Mounted while open; hidden (state kept) while one of its child modals is open. */}
            {spotModalOpen && selectedSpot && (
                <ParkingSpotDetailModal
                    key={selectedSpot.key}
                    show={!spotChildModalOpen}
                    onHide={() => setSpotModalOpen(false)}
                    building={currentBuilding}
                    area={area}
                    spot={selectedSpot}
                    isAdmin={isAdmin}
                    submitting={submitting}
                    hasDrawing={hasDrawing}
                    isPlacedOnCurrentDrawing={!!(currentBlueprintKey && selectedSpot.blueprintLocations?.[currentBlueprintKey])}
                    lease={selectedLease}
                    tenantName={selectedLease ? getTenantName(selectedLease.tenantKey) : undefined}
                    endedLeases={selectedEndedLeases}
                    getTenantName={getTenantName}
                    leasesLink={isAdmin && buildingKey ? leasesPath(buildingKey, selectedSpot.currentLeaseKey) : undefined}
                    onNewLease={() => setShowLeaseModal(true)}
                    onRentFromQueue={(entry, reason) => setQueuePick({ entry, reason })}
                    onAddToQueue={() => setAddToQueuePrefill(entryPrefillForSpot({ buildingKey: currentBuilding.key, areaKey: area.key, spotKey: selectedSpot.key, spot: selectedSpot }))}
                    queueReloadKey={queueReloadKey}
                    onEdit={() => setShowSpotModal('edit')}
                    onDelete={() => setConfirm({ kind: 'spot', spotKey: selectedSpot.key })}
                    onStartPlace={() => {
                        setSpotModalOpen(false);
                        setPlacement({ kind: 'place', spotKey: selectedSpot.key });
                    }}
                    chargerLink={`/building/${buildingKey}/component/${selectedSpot.chargerComponentKey}`}
                />
            )}

            <ParkingAreaModal
                show={showAreaModal}
                onHide={() => setShowAreaModal(false)}
                onSubmit={handleAreaSubmit}
                area={area}
                structure={structure}
                onCreateRoom={parking.createRoom}
                submitting={submitting}
            />

            <ParkingSpotModal
                show={showSpotModal !== 'closed'}
                onHide={handleSpotModalHide}
                onSubmit={handleSpotSubmit}
                building={currentBuilding}
                area={area}
                areas={areas}
                spot={showSpotModal === 'edit' ? selectedSpot : undefined}
                photoActions={showSpotModal === 'edit' && selectedSpot ? {
                    photos: selectedSpot.photos || {},
                    buildingKey: currentBuilding.key,
                    onDeletePhoto: handleDeletePhoto,
                    onUploadPhotos: uploader => parking.uploadSpotPhotos(areaKey, selectedSpot.key, uploader),
                } : undefined}
                initialValues={newSpotInitialValues}
                submitting={submitting}
                onRemoveLocation={showSpotModal === 'edit' && selectedSpot && currentBlueprintKey && selectedSpot.blueprintLocations?.[currentBlueprintKey]
                    ? () => parking.setSpotLocation(areaKey, selectedSpot.key, currentBlueprintKey, null)
                    : undefined}
            />

            {showLeaseModal && selectedSpot && (
                <LeaseModal
                    show={showLeaseModal}
                    onHide={() => setShowLeaseModal(false)}
                    onSubmit={handleLeaseSubmit}
                    buildingKey={buildingKey}
                    areas={[area]}
                    buildingLeases={leaseHook.leases}
                    lockedSpot={{ areaKey, spotKey: selectedSpot.key }}
                    submitting={leaseHook.submitting}
                />
            )}
            {queuePick && selectedSpot && (
                <LeaseModal
                    show
                    onHide={() => setQueuePick(null)}
                    onSubmit={handleQueueLeaseSubmit}
                    buildingKey={buildingKey}
                    areas={[area]}
                    buildingLeases={leaseHook.leases}
                    lockedSpot={{ areaKey, spotKey: selectedSpot.key }}
                    initialTenantKey={queuePick.entry.tenantKey}
                    initialStartDate={suggestedLeaseStart(leaseHook.leases.filter(l =>
                        l.object.type === LeaseObjectType.ParkingSpot && l.object.areaKey === areaKey && l.object.key === selectedSpot.key))}
                    submitting={leaseHook.submitting || queueMutations.submitting}
                />
            )}
            {addToQueuePrefill && (
                <AddToQueueModal
                    onHide={() => { setAddToQueuePrefill(null); queueMutations.clearError(); }}
                    onSubmit={handleAddToQueue}
                    prefill={addToQueuePrefill}
                    submitting={queueMutations.submitting}
                    error={queueMutations.error}
                />
            )}
            <LeaseQueueCloseModal lease={queueCheckLease} onDone={() => setQueueCheckLease(null)} />

            <BlueprintUploadModal
                isOpen={showUpload}
                onClose={() => { setShowUpload(false); setDroppedFile(null); }}
                deferPointer
                onDeferredUpload={handleDeferredUpload}
                beforeUpload={() => parkingApi.assertAdminForDrawings()}
                buildingKey={buildingKey}
                authorUid={user?.uid || ''}
                initialFile={droppedFile}
                target={ownUploadTarget}
            />

            {relocation && (
                <BlueprintRelocationWizard
                    building={currentBuilding}
                    source={relocation.source}
                    target={relocation.target}
                    items={relocation.items}
                    onCommit={handleRelocationCommit}
                    onAbort={handleRelocationAbort}
                />
            )}

            <Modal show={confirm !== null} onHide={cancelConfirm} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Är du säker?</Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmText}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelConfirm}>Avbryt</Button>
                    <Button variant={confirmIsDrawingChange ? 'warning' : 'danger'} onClick={handleConfirm}>
                        {confirmIsDrawingChange ? 'Fortsätt' : 'Ta bort'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ParkingAreaPage;
